import React, { useState, useRef, useEffect } from "react";
import { InfiniteLoader, AutoSizer, Table, Column, defaultTableRowRenderer } from "react-virtualized";
import { useTable, useGroupBy, useExpanded, useFlexLayout, useSortBy } from "react-table";
import { Table as SementicTable, Icon } from "semantic-ui-react";
import CurrencyFormat from "react-currency-format";
import { SafeMoment } from "./SafeMoment";
import { capitalizeFirstLetter } from "../utils/functions";

import "react-virtualized/styles.css"; // only needs to be imported once
import "./VZenTable.scss";

interface IVZenTable {
  columns: any;
  data: any;
  initialState?: any;
  containerProps?: any;
  infiniteLoaderProps?: any;
  tableProps?: any;
  useTableProps?: any;
  sortBy?: string;
  sortDirection?: string;
}

export const ExpandableVZenTable = ({
  columns,
  data,
  containerProps,
  infiniteLoaderProps,
  tableProps,
  useTableProps,
}: IVZenTable) => {
  const { headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data,
      ...useTableProps,
    },
    useGroupBy,
    useSortBy,
    useExpanded,
    useFlexLayout,
  );
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const tableRef: any = useRef();
  const _getRowHeight = ({ index }: { index: any }) => (index === selectedIndex ? 400 : 48);
  const rowRenderer = (props: any) => {
    const { index, style, className, key, rowData } = props;
    if (index === selectedIndex) {
      return (
        <div
          style={{ ...style, display: "flex", flexDirection: "column" }}
          className={`${className} ${data[rowData.index].transactions.length === 0 ? "" : "expand"}`}
          key={key}
        >
          {defaultTableRowRenderer({
            ...props,
            style: { width: style.width, height: 56, backgroundColor: "#dcdfec" },
          })}
          <div className={data[rowData.index].transactions.length === 0 ? "no-data" : "sub-table"}>
            {data[rowData.index].transactions.length === 0 ? (
              <></>
            ) : (
              <SementicTable singleLine basic>
                <SementicTable.Header>
                  <SementicTable.Row>
                    <SementicTable.HeaderCell>Date</SementicTable.HeaderCell>
                    <SementicTable.HeaderCell>Type</SementicTable.HeaderCell>
                    <SementicTable.HeaderCell>Transaction Number</SementicTable.HeaderCell>
                    <SementicTable.HeaderCell>App Type</SementicTable.HeaderCell>
                    <SementicTable.HeaderCell>App ID</SementicTable.HeaderCell>
                    <SementicTable.HeaderCell>Applicant</SementicTable.HeaderCell>
                    <SementicTable.HeaderCell style={{ borderRadius: "0 8px 0 0" }}>Amount</SementicTable.HeaderCell>
                  </SementicTable.Row>
                </SementicTable.Header>

                <SementicTable.Body>
                  {data[rowData.index].transactions.map((row: any, index: number) => (
                    <SementicTable.Row key={index}>
                      <SementicTable.Cell>
                        <SafeMoment format='MMM D, YYYY'>{row.confirmed_at || ""}</SafeMoment>
                      </SementicTable.Cell>
                      <SementicTable.Cell>{capitalizeFirstLetter(row.type || "")}</SementicTable.Cell>
                      <SementicTable.Cell>{row.transaction_number}</SementicTable.Cell>
                      <SementicTable.Cell>{row.permits_type_name}</SementicTable.Cell>
                      <SementicTable.Cell>{(row.application_id || "").match(/.{3}/g)?.join("-")}</SementicTable.Cell>
                      <SementicTable.Cell>{row.payer_name}</SementicTable.Cell>
                      <SementicTable.Cell>
                        <CurrencyFormat
                          value={(row.amount || 0) / 100.0}
                          fixedDecimalScale={true}
                          decimalScale={2}
                          displayType={"text"}
                          thousandSeparator={true}
                        />
                      </SementicTable.Cell>
                    </SementicTable.Row>
                  ))}
                </SementicTable.Body>
              </SementicTable>
            )}
          </div>
        </div>
      );
    }
    return defaultTableRowRenderer(props);
  };
  useEffect(() => {
    tableRef.current.recomputeRowHeights();
  }, [selectedIndex]);

  return (
    <div className='VZenTable Expandable' {...containerProps}>
      <InfiniteLoader {...infiniteLoaderProps}>
        {({ onRowsRendered }) => (
          <AutoSizer>
            {({ width, height }) => (
              <Table
                {...tableProps}
                ref={tableRef}
                onRowsRendered={(data) => {
                  onRowsRendered(data);
                  if (tableProps.onRowsRendered) {
                    tableProps.onRowsRendered(data);
                  }
                }}
                overscanRowCount={10}
                rowHeight={_getRowHeight}
                rowGetter={({ index }) => {
                  const row = rows[index];
                  prepareRow(row);
                  return row;
                }}
                rowCount={rows.length}
                height={height}
                width={width}
                rowRenderer={rowRenderer}
                onRowClick={(data: any) => {
                  // If there are no transactions, do not allow the row to be clicked
                  if (data.rowData.original.transactions.length === 0) {
                    return;
                  } else if (selectedIndex === data.index) {
                    // Close the row if it is already open
                    setSelectedIndex(-1);
                  } else {
                    // Open the row
                    setSelectedIndex(data.index);
                  }
                }}
              >
                {headerGroups[0].headers.map((column: any, id: number) => (
                  <Column
                    key={`${column.id}-${id}`}
                    dataKey={column.id}
                    width={parseInt(column.width)}
                    flexGrow={column.flexGrow}
                    flexShrink={column.flexShrink}
                    style={column.style}
                    headerRenderer={() => {
                      return (
                        <div role='button' tabIndex={-1}>
                          {column.render("Header")}
                        </div>
                      );
                    }}
                    cellRenderer={({ columnIndex, rowData }) => {
                      // Show the caret-down icon if the row is selected
                      if (columnIndex === 0 && selectedIndex === rowData.index) {
                        return (
                          <div className='tw-ml-1'>
                            <Icon name={"caret down"} />
                          </div>
                        );
                      } else if (selectedIndex === rowData.index) {
                        return <div className='tw-ml-1.5'>{rowData.cells[columnIndex].render("Cell")}</div>;
                      } else {
                        return rowData.cells[columnIndex].render("Cell");
                      }
                    }}
                  />
                ))}
              </Table>
            )}
          </AutoSizer>
        )}
      </InfiniteLoader>
    </div>
  );
};
